import React, { useState, Component } from 'react';
import { Calendar, Clock, Phone, MessageCircle, CheckCircle, ChevronRight } from 'lucide-react';
import { Button } from '../ui/Button';
const SERVICES = ['General Medical Consultations', 'Minor Surgical Procedures', 'Safe Medical Circumcision', 'Paternity Testing (DNA)', 'Immunizations', 'Pre-Employment Medical Examinations', 'Family Planning', 'Counseling Services', 'Chronic Disease Management', 'Physiotherapy', 'Optician Services', 'Antenatal Care', 'Postnatal Care', 'Laboratory Services', 'ECG Services', 'Ultrasound Services'];
const TIME_SLOTS = ['08:00 AM', '09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '02:00 PM', '03:00 PM', '04:00 PM', '05:00 PM'];
export function AppointmentSection() {
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    date: '',
    time: '',
    notes: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const update = (field: string, value: string) => setForm(prev => ({
    ...prev,
    [field]: value
  }));
  const isValid = form.name && form.phone && form.service && form.date;
  const whatsappLink = `https://wa.me/26773241613?text=${encodeURIComponent(`Hello Maisha Medical Center.\nI would like to book an appointment.\nName: ${form.name}\nPhone: ${form.phone}\nPreferred Service: ${form.service}\nPreferred Date: ${form.date}\nPreferred Time: ${form.time}\n\nPlease assist me.`)}`;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setSubmitted(true);
  };
  return <section className="relative py-24 overflow-hidden" data-id="element-59">
      {/* Background image */}
      <img src="https://cdn.magicpatterns.com/uploads/qbGmSXCEC9QH6TthgTKZco/maisha_edited_6.jpg" alt="Maisha Medical Center reception and waiting area" className="absolute inset-0 w-full h-full object-cover" data-id="element-60" />
      {/* Brand navy color overlay */}
      <div className="absolute inset-0 bg-brand-navy/90" data-id="element-61" />
      <div className="absolute inset-0 bg-gradient-to-r from-brand-navy via-brand-navy/85 to-brand-navy/70" data-id="element-62" />

      <div className="relative z-10 max-w-brand mx-auto px-4 sm:px-6 lg:px-8" data-id="element-63">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center" data-id="element-64">
          {/* Intro copy */}
          <div className="w-full lg:w-1/2 text-white" data-id="element-65">
            <span className="font-semibold tracking-wider text-sm uppercase mb-3 block text-[#FFFFFF]" data-id="element-66">
              Book Your Visit
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-6 leading-tight" data-id="element-67">
              Request an Appointment Today
            </h2>
            <p className="text-slate-200 text-lg leading-relaxed mb-8 max-w-xl" data-id="element-68">
              Schedule your visit to Maisha Medical Center in just a few
              moments. Fill in your details and our reception team will confirm
              your appointment promptly.
            </p>

            <ul className="space-y-4 mb-10" data-id="element-69">
              {['Trusted, experienced healthcare professionals', 'Comprehensive services under one roof', 'Fast confirmation via call or WhatsApp'].map(item => <li key={item} className="flex items-center gap-3" data-id="element-70">
                  <CheckCircle className="w-6 h-6 text-brand-gold shrink-0" data-id="element-71" />
                  <span className="text-slate-100 font-medium" data-id="element-72">{item}</span>
                </li>)}
            </ul>

            <div className="flex flex-col sm:flex-row gap-6" data-id="element-73">
              <a href="tel:+2676540244" className="flex items-center gap-3 text-white group" data-id="element-74">
                <span className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-brand-red transition-colors" data-id="element-75">
                  <Phone className="w-5 h-5" data-id="element-76" />
                </span>
                <span data-id="element-77">
                  <span className="block text-xs text-slate-300" data-id="element-78">Call us</span>
                  <span className="font-semibold" data-id="element-79">+267 6540244</span>
                </span>
              </a>
              <div className="flex items-center gap-3 text-white" data-id="element-80">
                <span className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center" data-id="element-81">
                  <Clock className="w-5 h-5 text-brand-gold" data-id="element-82" />
                </span>
                <span data-id="element-83">
                  <span className="block text-xs text-slate-300" data-id="element-84">
                    Operating hours
                  </span>
                  <span className="font-semibold" data-id="element-85">Mon–Fri 8am–6pm</span>
                </span>
              </div>
            </div>
          </div>

          {/* Form card */}
          <div className="w-full lg:w-1/2" data-id="element-86">
            <div className="bg-white rounded-2xl shadow-soft-lg p-6 md:p-8" data-id="element-87">
              {submitted ? <div className="text-center py-8" data-id="element-88">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-89">
                    <CheckCircle className="w-8 h-8 text-green-600" data-id="element-90" />
                  </div>
                  <h3 className="text-2xl font-heading font-bold text-brand-navy mb-3" data-id="element-91">
                    Request Received!
                  </h3>
                  <p className="text-slate-600 mb-8" data-id="element-92">
                    Thank you, {form.name}. Our team will contact you shortly to
                    confirm your appointment for{' '}
                    <strong className="text-brand-navy" data-id="element-93">{form.service}</strong>.
                  </p>
                  <Button href={whatsappLink} target="_blank" variant="primary" fullWidth icon={<MessageCircle className="w-5 h-5" data-id="element-95" />} className="bg-[#25D366] hover:bg-[#20bd5a] focus:ring-[#25D366]" data-id="element-94">
                    Confirm on WhatsApp
                  </Button>
                </div> : <>
                  <div className="flex items-center gap-3 mb-6" data-id="element-96">
                    <div className="w-11 h-11 rounded-xl bg-brand-red/10 flex items-center justify-center text-brand-red" data-id="element-97">
                      <Calendar className="w-6 h-6" data-id="element-98" />
                    </div>
                    <h3 className="text-xl font-heading font-bold text-brand-navy" data-id="element-99">
                      Appointment Details
                    </h3>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4" data-id="element-100">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-101">
                      <div data-id="element-102">
                        <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-103">
                          Full Name *
                        </label>
                        <input required type="text" value={form.name} onChange={e => update('name', e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red transition-all" placeholder="John Doe" data-id="element-104" />
                      </div>
                      <div data-id="element-105">
                        <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-106">
                          Phone Number *
                        </label>
                        <input required type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red transition-all" placeholder="+267 ..." data-id="element-107" />
                      </div>
                    </div>

                    <div data-id="element-108">
                      <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-109">
                        Email Address
                      </label>
                      <input type="email" value={form.email} onChange={e => update('email', e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red transition-all" placeholder="john@example.com" data-id="element-110" />
                    </div>

                    <div data-id="element-111">
                      <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-112">
                        Preferred Service *
                      </label>
                      <select required value={form.service} onChange={e => update('service', e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red transition-all bg-white text-slate-700" data-id="element-113">
                        <option value="" disabled data-id="element-114">
                          Select a service
                        </option>
                        {SERVICES.map(service => <option key={service} value={service} data-id="element-115">
                            {service}
                          </option>)}
                      </select>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-116">
                      <div data-id="element-117">
                        <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-118">
                          Preferred Date *
                        </label>
                        <input required type="date" value={form.date} onChange={e => update('date', e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red transition-all text-slate-700" data-id="element-119" />
                      </div>
                      <div data-id="element-120">
                        <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-121">
                          Preferred Time
                        </label>
                        <select value={form.time} onChange={e => update('time', e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red transition-all bg-white text-slate-700" data-id="element-122">
                          <option value="" data-id="element-123">Any time</option>
                          {TIME_SLOTS.map(slot => <option key={slot} value={slot} data-id="element-124">
                              {slot}
                            </option>)}
                        </select>
                      </div>
                    </div>

                    <div data-id="element-125">
                      <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-126">
                        Additional Notes
                      </label>
                      <textarea rows={2} value={form.notes} onChange={e => update('notes', e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red transition-all resize-none" placeholder="Any specific concerns or symptoms?" data-id="element-127" />
                    </div>

                    <Button type="submit" variant="primary" fullWidth disabled={!isValid} icon={<ChevronRight className="w-5 h-5" data-id="element-129" />} data-id="element-128">
                      Request Appointment
                    </Button>
                    <p className="text-xs text-slate-400 text-center" data-id="element-130">
                      Prefer the full booking experience?{' '}
                      <a href="/book" className="text-brand-red font-medium" data-id="element-131">
                        Book step-by-step
                      </a>
                    </p>
                  </form>
                </>}
            </div>
          </div>
        </div>
      </div>
    </section>;
}